import * as WorldMapData from '@highcharts/map-collection/custom/world.geo.json';
import * as widgetActions from '../../../actions/widget.actions';

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  firstValueFrom,
  map,
  switchMap,
  take,
  tap,
} from 'rxjs';
import {
  DataObject,
  Feed,
  FilterItem,
  ObjectDefinition,
  ObjectPermission,
  Post,
  User,
  Widget,
} from '../../../models/core.models';
import { DialogCloseReason, DialogService } from '@sui/dialog';
import {
  FeedSettingsService,
  PropertyViewConfig,
} from 'src/app/core/services/feed-settings.service';
import {
  NomLanguages,
  NomObjectClass,
  NomObjectTypes,
  PopupType,
  WidgetType,
} from '../../../constants';
import { Store, select } from '@ngrx/store';
import { WidgetService } from '../../../services/widget.service';
import { WidgetApiService } from 'src/app/api/services/widget-api.service';
import { WidgetExportFormat } from 'src/app/api/serialize/widget.serialize';
import { cssToken } from 'src/app/shared/utils/highcharts-theme.utils';
import {
  activateFilters,
  selectFilterItem,
} from 'src/app/core/actions/feed.actions';
import {
  getContentFilters,
  getContentFiltersForPage,
  selectCurrentObject,
  selectFeedActiveFilterItems,
  selectUser,
  selectWidgetViewConfig,
} from 'src/app/core/reducers';

import { AppFeatures } from 'src/app/core/app.features';
import { AppState } from 'src/app/reducers';
import { ConfirmDialogComponent } from '../../popups/confirm-dialog/confirm-dialog.component';
import { DialogCloseOptions } from '@sui/dialog';
import { Highcharts } from '../../../highcharts-shared';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { LoggerService } from '@services/logger.service';
import { MoveCopyWidgetDialogComponent } from '../../../../shared/components/move-copy-widget-dialog/move-copy-widget-dialog.component';
import {
  SwapWidgetDialogComponent,
  SwapWidgetDialogOutput,
} from '../../popups/swap-widget-dialog/swap-widget-dialog.component';
import { ObjectActionsService } from 'src/app/core/services/object-actions.service';
import { WidgetPersistenceService } from 'src/app/core/services/widget-persistence.service';
import { WidgetViewStateService } from 'src/app/core/services/widget-view-state.service';
import { ObjectEditorService } from '../../../services/object-editor.service';
import { PickerDataobjectsDialogComponent } from '../../popups/picker-dataobjects-dialog/picker-dataobjects-dialog.component';
import { SidebarButtonConfig } from '../../sidebar/sidebar.component';
import { SidebarService } from 'src/app/core/services/sidebar.service';
import { TranslateService } from '@ngx-translate/core';
import { UserFacadeService } from '@services/user-facade.service';
import { UtilsService } from 'src/app/core/services/utils.service';
import { WidgetOperation } from '../../../models/widget-operations';
import { WidgetViewProperties } from 'src/app/api/services/feed-api.service';
import { readableTextColor } from 'src/app/shared/utils/color-utils';
import {
  selectCurrentObjectCacheEnabled,
  selectSelectedObjects,
  selectWidgetsLanguageId,
} from './../../../reducers/index';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  WidgetDataScope,
  injectWidgetDataQuery,
} from 'src/app/api/queries/widget.queries';

interface CardTool {
  icon: IconProp;
  tooltip?: string;
  action: () => void;
  permission: AppFeatures;
  objectPermission?: ObjectPermission[];
}

interface DropdownItem {
  id?: string;
  name?: string;
  icon?: IconProp;
  action?: () => void;
  permission?: AppFeatures;
  objectPermission?: ObjectPermission[];
  type?: 'export-option' | 'tool';
  isTitle?: boolean;
  isSeparator?: boolean;
}

@Component({
  selector: 'app-widget-card',
  templateUrl: './widget-card.component.html',
  styleUrls: ['./widget-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class WidgetCardComponent
  implements OnInit, AfterViewInit, OnChanges, OnDestroy
{
  @ViewChild('widgetContent', { static: false }) widgetContent: ElementRef;

  @Input() widget: Widget;
  @Input() isPlaceholderSelected: boolean;
  @Input() widgetPreview: boolean = false;
  @Input() currentObject: DataObject;
  @Input() objectActionService: ObjectActionsService;
  @Input() editMode: boolean = true;
  @Input() user: User;
  @Input() viewProperties: WidgetViewProperties;
  @Input() presentationModeActive: boolean = false;
  @Input() resizingWidgetId$: BehaviorSubject<string | null>;
  @Input() isDragging$: BehaviorSubject<boolean>;
  @Input() searchString: string = '';
  @Input() postCardViewConfig: PropertyViewConfig[];
  @Input() showLoading: boolean = true;

  private readonly widgetPersistence = inject(WidgetPersistenceService);
  private readonly widgetViewState = inject(WidgetViewStateService);

  // ---- per-widget data query --------------------------------------------------
  // * the card owns its data; parents pass metadata only. Only preview cards
  // * still receive data merged into @Input() widget - see `usesDataQuery`.
  private readonly widgetIdSignal = signal<string | null>(null);
  private readonly dataRequestScope = signal<Omit<
    WidgetDataScope,
    'widgetId'
  > | null>(null);
  // * latches on the first time the card scrolls into view - the lazy-load guard
  private readonly hasBeenVisible = signal(false);
  private readonly dataQueryEnabled = signal(false);
  private readonly prefetchedExternally = signal(false);
  // * null = the global content filters; a page id = that Dashboard Page's
  // * (Presentation Mode renders every Page at once, each with its own period)
  private readonly contentFiltersPageId$ = new BehaviorSubject<number | null>(
    null,
  );

  private readonly widgetDataScope = computed<WidgetDataScope | null>(() => {
    const widgetId = this.widgetIdSignal();
    const scope = this.dataRequestScope();

    return widgetId && scope ? { widgetId, ...scope } : null;
  });

  readonly dataQuery = injectWidgetDataQuery(
    this.widgetDataScope,
    // * Presentation Mode cards never fetch for themselves - every slide is
    // * mounted at once, so the component prefetches page by page into the same
    // * key and these cards just observe it
    computed(
      () =>
        this.dataQueryEnabled() &&
        this.hasBeenVisible() &&
        !this.prefetchedExternally(),
    ),
  );

  private readonly widgetData$ = toObservable(
    computed(() => this.dataQuery.data()),
  );

  /** Preview cards get their data through `@Input() widget`. */
  private get usesDataQuery(): boolean {
    return (
      !this.widgetPreview &&
      this.widget?.widget_type_id !== WidgetType.WIDGET_TYPE_PLACEHOLDER
    );
  }

  get widgetData(): any {
    return this.usesDataQuery ? this.dataQuery.data() : this.widget?.data;
  }

  get isWidgetLoading(): boolean {
    return this.usesDataQuery
      ? this.dataQuery.isFetching()
      : !!this.widget?.loading;
  }

  @Output() zoomChanged = new EventEmitter<{
    zoom: number;
    center: [number, number];
  }>();

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.widgetService.widgetResized.emit();
  }

  private viewPropertiesSubject = new BehaviorSubject<
    WidgetViewProperties | undefined
  >(undefined);
  readonly viewProperties$ = this.viewPropertiesSubject.asObservable();

  // * fed from ngOnChanges, like hasFilters$/hasPivots$ below
  private readonly isPivotable$ = new BehaviorSubject<boolean | undefined>(
    undefined,
  );

  readonly showPivots$ = combineLatest([
    this.viewProperties$.pipe(map((vp) => vp?.widget_display_attached_pivots)),
    this.isPivotable$,
  ]).pipe(
    map(
      ([displayPivots, isPivotable]) => displayPivots && isPivotable === true,
    ),
  );

  readonly showFilters$ = this.viewProperties$.pipe(
    map((vp) => vp?.widget_display_attached_filters),
  );

  readonly showDataSources$ = this.viewProperties$.pipe(
    map((vp) => vp?.widget_display_attached_data_sources),
  );

  readonly hasDatasources$ = new BehaviorSubject<boolean>(false);
  readonly hasFilters$ = new BehaviorSubject<boolean>(false);
  readonly hasPivots$ = new BehaviorSubject<boolean>(false);
  readonly datasources$ = new BehaviorSubject<any[]>([]);

  readonly showDimensions$ = combineLatest([
    this.hasFilters$,
    this.hasPivots$,
    this.showPivots$,
    this.showFilters$,
  ]).pipe(
    map(
      ([hasFilters, hasPivots, showPivots, showFilters]) =>
        (showPivots || showFilters) && (hasFilters || hasPivots),
    ),
  );

  WidgetType = WidgetType;

  isSelected$: Observable<boolean>;

  Highcharts: typeof Highcharts = Highcharts; // required for <highcharts-chart>
  widgetTypeId: WidgetType;
  chartConstructor = 'chart'; // Map type

  widgetContentWidth: number;
  widgetContentHeight: number;

  widgetItems: Array<any> | any;
  posts: Array<Post>;
  noContentMsg = this.translateService.instant(
    'widget-card.message.no-content-matching-the-current-search-criteria',
  );

  tableData: any;
  chartOptions: any[] = []; // Chart options
  currentChartIndex = 0;
  isFiltersSelected: boolean;
  isPivotsSelected: boolean;
  chartRef: Highcharts.Chart;

  supportsDataSources = false;
  widgetViewIninitialized = false;
  canEditWidget: boolean = false;
  hasResults: boolean = true;

  get headerColor(): string | null {
    return this.widget?.settings?.widget_header_color || null;
  }

  // * readable text/icon color for the header when a custom background color is set
  get headerTextColor(): string | null {
    return this.headerColor ? readableTextColor(this.headerColor) : null;
  }

  get hasCustomPeriod(): boolean {
    return !!this.widget?.settings?.custom_period;
  }

  get hasOverrideOutputSettings(): boolean {
    return !!this.widget?.settings?.override_output_settings;
  }

  get periodTimeStart(): number | null {
    return this.widget?.settings?.feed?.content_filters?.time_start ?? null;
  }

  get periodTimeEnd(): number | null {
    return this.widget?.settings?.feed?.content_filters?.time_end ?? null;
  }

  private selectedCountryRawValue: number | string | null = null;

  private resizeObserver: any;

  isHighchart: boolean = false;
  showChart: boolean = true; // Used to force re-rendering of the chart when options change

  dropdownItems: DropdownItem[] = [
    {
      isTitle: true,
      name: this.translateService.instant('widget-card.title.actions-:'),
    },
    {
      isSeparator: true,
    },
    {
      name: this.translateService.instant('widget-card.tooltip.reload'),
      icon: ['fas', 'refresh'],
      action: () => this.reloadWidget(),
      permission: AppFeatures.WIDGET_ROOT,
      objectPermission: ['can_view'],
      type: 'tool',
    },
    {
      name: this.translateService.instant('widget-card.tooltip.print'),
      icon: ['fas', 'print'],
      action: () => this.printWidget(),
      permission: AppFeatures.WIDGET_EXPORT,
      objectPermission: ['can_view'],
      type: 'tool',
    },
    // {
    //   name: this.translateService.instant('widget-card.tooltip.download'),
    //   icon: ['fas', 'download'],
    //   action: () => this.downloadWidget(),
    //   permission: AppFeatures.WIDGET_EXPORT,
    //   objectPermission: ['can_view'],
    //   type: 'tool',
    // },
    {
      name: this.translateService.instant('widget-card.tooltip.export-csv'),
      icon: ['fas', 'file-csv'],
      action: () => this.exportChart('csv'),
      permission: AppFeatures.WIDGET_EXPORT,
      objectPermission: ['can_view'],
      type: 'tool',
    },
    {
      name: this.translateService.instant(
        'widget-card.tooltip.export-to-excel',
      ),
      icon: ['far', 'file-excel'],
      action: () => this.exportChart('xlsx'),
      permission: AppFeatures.WIDGET_EXPORT,
      objectPermission: ['can_view'],
      type: 'tool',
    },
  ];
  exportOptions: DropdownItem[] = [
    { isSeparator: true },
    {
      isTitle: true,
      name: this.translateService.instant('widget-card.title.export-options-:'),
    },
    {
      isSeparator: true,
    },
    // {
    //   id: 'print',
    //   name: this.translateService.instant(
    //     'widget-card.chart-export-option.print',
    //   ),
    //   icon: ['fas', 'print'],
    //   type: 'export-option',
    // },
    {
      id: 'png',
      name: this.translateService.instant(
        'widget-card.chart-export-option.download-png',
      ),
      icon: ['fas', 'file-image'],
      type: 'export-option',
    },
    {
      id: 'jpeg',
      name: this.translateService.instant(
        'widget-card.chart-export-option.download-jpeg',
      ),
      icon: ['fas', 'file-image'],
      type: 'export-option',
    },
    {
      id: 'svg',
      name: this.translateService.instant(
        'widget-card.chart-export-option.download-svg',
      ),
      icon: ['fas', 'file-code'],
      type: 'export-option',
    },
    // {
    //   id: 'pdf',
    //   name: this.translateService.instant(
    //     'widget-card.chart-export-option.download-pdf',
    //   ),
    //   icon: ['fas', 'file-pdf'],
    //   type: 'export-option',
    // },

    // {
    //   id: 'csv',
    //   name: this.translateService.instant(
    //     'widget-card.chart-export-option.download-csv',
    //   ),
    //   icon: ['fas', 'file-csv'],
    //   type: 'export-option',
    // }
    // {
    //   id: 'xlsx',
    //   name: this.translateService.instant(
    //     'widget-card.chart-export-option.download-xlsx',
    //   ),
    //   icon: ['fas', 'file-excel'],
    //   type: 'export-option',
    // },
  ];

  private readonly allCardTools: CardTool[] = [
    {
      icon: ['fas', 'exchange-alt'],
      tooltip: this.translateService.instant(
        'widget-card.tooltip.swap-widget-type',
      ),
      action: () => this.swapWidget(),
      permission: AppFeatures.WIDGET_ROOT,
      objectPermission: ['can_edit'],
    },
    {
      icon: ['far', 'copy'],
      tooltip: this.translateService.instant(
        'widget-card.tooltip.copy-/-move-widget',
      ),
      action: () => this.duplicateWidget(),
      permission: AppFeatures.WIDGET_DUPLICATE,
      objectPermission: ['can_view'],
    },
    {
      icon: ['fas', 'gear'],
      tooltip: this.translateService.instant('widget-card.tooltip.settings'),
      action: () => this.editWidget(),
      permission: AppFeatures.WIDGET_DUPLICATE,
      objectPermission: ['can_edit'],
    },
    {
      icon: ['far', 'times-circle'],
      tooltip: this.translateService.instant(
        'widget-card.tooltip.remove-widget',
      ),
      action: () => this.deleteWidget(),
      permission: AppFeatures.WIDGET_ROOT,
      objectPermission: ['can_edit'],
    },
  ];

  // 2) the filtered, bindable array
  cardTools: CardTool[] = [];

  windowWidth: number = window.innerWidth;
  windowHeight: number = window.innerHeight;

  private destroyRef = inject(DestroyRef);
  private dialogService = inject(DialogService);
  private widgetApi = inject(WidgetApiService);

  constructor(
    private store: Store<AppState>,
    private widgetService: WidgetService,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
    private editService: ObjectEditorService,
    private utilsService: UtilsService,
    private sidebarService: SidebarService,
    private logger: LoggerService,
    private renderer: Renderer2,
    protected userFacade: UserFacadeService,
    private feedSettingsService: FeedSettingsService,
  ) {
    this.store
      .select(selectWidgetViewConfig)
      .pipe(
        tap((config) => {
          this.isFiltersSelected = config.some(
            (item) => item.path === 'filters' && item.selected,
          );
          this.isPivotsSelected = config.some(
            (item) => item.path === 'pivots' && item.selected,
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    // * the three store reads that key this card's data request
    combineLatest([
      this.contentFiltersPageId$.pipe(
        distinctUntilChanged(),
        switchMap((pageId) =>
          pageId === null
            ? this.store.select(getContentFilters)
            : this.store.select(getContentFiltersForPage(pageId)),
        ),
      ),
      this.store.select(selectWidgetsLanguageId),
      this.store.select(selectCurrentObjectCacheEnabled),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([contentFilters, languageId, cacheEnabled]) =>
        this.dataRequestScope.set({ contentFilters, languageId, cacheEnabled }),
      );
  }

  private async initializePostCardViewConfig(): Promise<void> {
    if (this.widgetPreview) {
      return;
    }
    const user =
      this.user || (await firstValueFrom(this.store.select(selectUser)));
    const objectTypeId: number =
      this.widget?.datasources?.[0]?.object_type_id ??
      NomObjectTypes.ONLINE_FEED;
    const feedConfig = await this.feedSettingsService.getFeedConfig(
      objectTypeId,
      user,
    );
    const defaultSelected = feedConfig
      .filter((c) => c.selected)
      .map((c) => c.path);
    const selectedPaths =
      this.widget.settings?.feed?.view_properties || defaultSelected;

    this.postCardViewConfig =
      this.feedSettingsService.buildViewConfigFromSelectedPaths(
        feedConfig,
        selectedPaths,
      );
  }

  private refreshCardTools(): void {
    this.cardTools = this.allCardTools.filter(
      (tool) =>
        this.userFacade.hasAllObjectPermissionsSync(
          this.currentObject,
          tool.objectPermission ?? [],
        ) &&
        (!tool.permission ||
          this.userFacade.hasPermissionSync(tool.permission) === true),
    );

    this.dropdownItems = this.dropdownItems.filter((item) => {
      if (item.type === 'tool') {
        return (
          this.userFacade.hasAllObjectPermissionsSync(
            this.currentObject,
            item.objectPermission ?? [],
          ) &&
          (!item.permission ||
            this.userFacade.hasPermissionSync(item.permission) === true)
        );
      } else {
        return true;
      }
    });
  }

  ngOnInit() {
    this.isHighchart = this.isChartOrMap();
    if (this.widget.widget_type_id === WidgetType.WIDGET_TYPE_CHART) {
      this.exportOptions.forEach((option) => this.dropdownItems.push(option));
    }
    if (this.widget) {
      this.canEditWidget = this.userFacade.hasAllObjectPermissionsSync(
        this.currentObject,
        ['can_edit'],
      );

      this.refreshCardTools();
      const widgetTypeId = this.widget.widget_type_id;
      const mapWidgetTypesWithNoDataSources = new Set([
        WidgetType.WIDGET_TYPE_SECTION_BREAK,
        WidgetType.WIDGET_TYPE_EXPORT_VARIABLES_SYSTEM,
        WidgetType.WIDGET_TYPE_EXPORT_VARIABLES_USER,
      ]);

      this.supportsDataSources =
        !mapWidgetTypesWithNoDataSources.has(widgetTypeId);
    }

    this.isSelected$ = this.store.select(selectSelectedObjects).pipe(
      takeUntilDestroyed(this.destroyRef),
      map((selectedObjects) => selectedObjects.has(this.widget.id)),
      distinctUntilChanged(),
    );

    this.widgetData$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        if (!this.usesDataQuery || !data) {
          return;
        }

        this.handleDataChange(data);
        // * markForCheck, not detectChanges: this fires from a signal effect that
        // * Angular runs while checking this view, so re-entering CD would be
        // * unsafe - marking is enough for the fields above to render
        this.cdr.markForCheck();
      });

    // * Listen for resize events to hide content during resize
    if (this.resizingWidgetId$) {
      this.resizingWidgetId$
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          map((id) => id === this.widget.id),
          distinctUntilChanged(),
          tap((isResizing) => {
            if (
              this.widgetContent &&
              // * Hide HTML/SUMMARY widgets when ANY widget is being resized
              (this.widget.widget_type_id === WidgetType.WIDGET_TYPE_HTML ||
                this.widget.widget_type_id === WidgetType.WIDGET_TYPE_SUMMARY)
            ) {
              this.widgetContent.nativeElement.style.display = isResizing
                ? 'none'
                : '';
            }
          }),
        )
        .subscribe();
    }

    // * Hide ALL HTML/SUMMARY widgets when ANY widget is being dragged
    if (this.isDragging$) {
      this.isDragging$
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          distinctUntilChanged(),
          tap((isDragging) => {
            if (
              this.widgetContent &&
              isDragging &&
              (this.widget.widget_type_id === WidgetType.WIDGET_TYPE_HTML ||
                this.widget.widget_type_id === WidgetType.WIDGET_TYPE_SUMMARY)
            ) {
              this.widgetContent.nativeElement.style.display = 'none';
            } else if (this.widgetContent && !isDragging) {
              this.widgetContent.nativeElement.style.display = '';
            }
          }),
        )
        .subscribe();
    }

    // * listen for Country Filter removal
    if (this.widgetTypeId === WidgetType.WIDGET_TYPE_GEO_MAP) {
      this.store
        .select(selectFeedActiveFilterItems)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          tap((activeFilters) => {
            const hasCountryFilter = activeFilters.some(
              (filter) => filter.property_uri === 'post_section_country_id',
            );

            // * if no Country Filter exists -> reset our state
            if (!hasCountryFilter) {
              this.selectedCountryRawValue = null;
            }
          }),
        )
        .subscribe();
    }

    //this.initializeFeedViewConfig();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['viewProperties']) {
      // this.logger.debug(
      //   'WidgetCardComponent: viewProperties changed',
      //   changes['viewProperties'].currentValue,
      // );
      this.viewPropertiesSubject.next(this.viewProperties);
    }

    if (changes['postCardViewConfig']) {
      this.postCardViewConfig = changes['postCardViewConfig'].currentValue;
    }

    // * keep the data query pointed at this widget. Outside the widget-change
    // * guard below: `presentationModeActive` flips on its own and decides both
    // * which content filters key this card and whether it fetches for itself
    this.widgetIdSignal.set(this.widget?.id ?? null);
    this.dataQueryEnabled.set(this.usesDataQuery);
    this.prefetchedExternally.set(this.presentationModeActive);
    this.contentFiltersPageId$.next(
      this.presentationModeActive ? (this.widget?.page_id ?? null) : null,
    );

    const widgetChange = changes['widget'];

    if (!widgetChange) {
      this.logger.debug(`WidgetCardComponent: no widget change`, changes);
      return;
    }

    const currentWidget = widgetChange.currentValue as Widget;

    // Update our observables when widget changes
    this.hasDatasources$.next(currentWidget?.datasources?.length > 0);
    this.hasFilters$.next(currentWidget?.dimensions_filter?.length > 0);
    this.hasPivots$.next(currentWidget?.dimensions_pivot?.length > 0);
    this.datasources$.next(currentWidget?.datasources || []);
    this.isPivotable$.next(currentWidget?.chart_type?.is_pivotable);

    const previousWidget = widgetChange.previousValue as Widget;

    if (
      widgetChange.firstChange ||
      currentWidget?.data !== previousWidget?.data ||
      currentWidget?.widget_type_id !== previousWidget?.widget_type_id ||
      currentWidget?.chart?.id !== previousWidget?.chart?.id ||
      currentWidget?.chart_type?.id !== previousWidget?.chart_type?.id
    ) {
      this.logger.debug('widget.data changed');
      this.widgetTypeId = currentWidget.widget_type_id;
      this.isHighchart = this.isChartOrMap();

      if (currentWidget?.data) {
        this.handleDataChange(currentWidget.data);
      }
      // reset view config to trigger re-evaluation of applicable properties in settings popup

      this.initializePostCardViewConfig();
    }

    this.cdr.detectChanges();
  }

  ngAfterViewInit(): void {
    this.widgetViewIninitialized = true;

    // * if we have HTML Widget data BUT no iframe was created yet -> (preview mode timing issue)
    if (
      this.widgetData &&
      (this.widgetTypeId === WidgetType.WIDGET_TYPE_HTML ||
        this.widgetTypeId === WidgetType.WIDGET_TYPE_SUMMARY) &&
      this.widgetContent?.nativeElement &&
      !this.widgetContent.nativeElement.querySelector(
        'iframe.html-widget-iframe',
      )
    ) {
      // * retry iframe creation now that ViewChild is available
      this.handleDataChange(this.widgetData);
    }

    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        this.updateChartDimensions(width, height);
      }
    });

    if (this.widgetContent?.nativeElement) {
      this.resizeObserver.observe(this.widgetContent.nativeElement);
    }

    this.widgetService.widgetResized
      .pipe(
        debounceTime(200),
        tap(() => {
          // * `chartRef` can outlive its DOM: the loader branch in the template
          // * removes <highcharts-chart> without going through
          // * resetWidgetState(), so a debounced resize can land on a detached
          // * chart - and Highcharts' reflow() reads container.parentNode.children
          if (
            this.widgetViewState.isVisible(this.widget.id) &&
            this.chartRef?.container?.parentNode
          ) {
            this.chartRef.reflow();
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  ngOnDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  private updateChartDimensions(width?: number, height?: number) {
    if (!this.chartRef || !this.isHighchart) return;

    // * if dimensions not provided -> get them from the Element
    if (!width || !height) {
      const rect = this.widgetContent.nativeElement.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
    }

    // * apply minimum dimensions
    width = Math.max(200, Math.floor(width));
    height = Math.max(200, Math.floor(height));

    // * update chart dimensions in a safe way - guard against timing issues
    try {
      if (this.chartRef && typeof this.chartRef.setSize === 'function') {
        this.chartRef.setSize(width, height, false);
      }
    } catch (error) {
      // ! chart might have been destroyed, clear the reference
      this.chartRef = null;
      return;
    }

    // * trigger redraw
    requestAnimationFrame(() => {
      // * guard against timing issues
      try {
        if (this.chartRef && typeof this.chartRef.reflow === 'function') {
          this.chartRef.reflow();
        }
      } catch (error) {
        // ! chart might have been destroyed during the requestAnimationFrame
        this.chartRef = null;
      }
    });
  }

  printWidget(): void {
    const printContent = this.widgetContent.nativeElement.innerHTML;

    const width = 900;
    const height = 700;

    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const printWindow = window.open(
      '',
      'printWindow',
      `popup=yes,width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`,
    );

    if (!printWindow) return;

    const styles = Array.from(
      document.querySelectorAll('style, link[rel="stylesheet"]'),
    )
      .map((el) => el.outerHTML)
      .join('\n');

    printWindow.document.write(`
    <html>
      <head>${styles}</head>
      <body>${printContent}</body>
    </html>
  `);

    printWindow.document.close();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 150);
  }

  downloadWidget() {
    this.exportChart('pdf');
  }

  exportToExcel() {
    this.exportChart('xlsx');
  }

  configurePopup(
    objectTypeIds: NomObjectTypes[],
    selectedObjectIds: Map<string, ObjectDefinition>,
    // * a `settings` patch for the picked ids, persisted by WidgetPersistenceService
    buildSettingsPatch: (objectIds: string[]) => Record<string, unknown>,
    popupType: PopupType,
  ) {
    const dialogRef = this.dialogService.openTabsDialog(
      PickerDataobjectsDialogComponent,
      {
        data: { objectTypeIds, selectedObjectIds, popupType },
        height: '70vh',
        width: '61vw',
        minWidth: 1220,
        maxWidth: 1550,
        closeOnClick: false,
        closeOnEscape: true,
      },
    );

    dialogRef.closed.subscribe((event: any) => {
      if (
        event.reason === DialogCloseReason.Save ||
        event.reason === DialogCloseReason.SaveAndClose
      ) {
        this.widgetPersistence.applySettings(
          [this.widget.id],
          buildSettingsPatch(event?.data),
        );
      }

      console.log(
        '%c Dialog closed with event:',
        'color: green; font-weight: bold;',
        event,
      );
    });
  }

  editFilter(dimension: Feed) {
    this.editService.loadFeedAndOpenPopup(dimension.id, 'search-builder');
  }

  configureFilters() {
    const objectMap = new Map<string, ObjectDefinition>(
      this.widget.dimensions_filter?.map((object) => [
        object.id,
        {
          object_class_id: object.object_class_id,
          object_type_id: object.object_type_id,
          object_id: object.id,
        },
      ]),
    );

    this.configurePopup(
      [NomObjectTypes.SEARCH_DIMENSION],
      objectMap,
      (objectIds) => ({ dimension_id_filter: objectIds }),
      PopupType.ATTACH_DIMENSIONS_AS_FILTERS,
    );
  }

  configurePivots() {
    const objectMap = new Map<string, ObjectDefinition>(
      this.widget.dimensions_pivot?.map((object) => [
        object.id,
        {
          object_class_id: object.object_class_id,
          object_type_id: object.object_type_id,
          object_id: object.id,
        },
      ]),
    );

    this.configurePopup(
      [NomObjectTypes.SEARCH_DIMENSION],
      objectMap,
      (objectIds) => ({ dimension_id_pivot: objectIds }),
      PopupType.ATTACH_DIMENSIONS_AS_PIVOTS,
    );
  }

  configureDatasourceObject(datasource: Feed) {
    this.editService.loadFeedAndOpenPopup(datasource.id, 'search-builder');
  }

  // * this is bound to the button for "noDatasources"
  configureDatasource() {
    const objectMap = new Map<string, ObjectDefinition>(
      this.widget.datasources?.map((object) => [
        object.object_id,
        {
          object_class_id: object.object_class_id,
          object_type_id: object.object_type_id,
          object_id: object.id,
        },
      ]),
    );

    this.configurePopup(
      [
        NomObjectTypes.ONLINE_FEED,
        NomObjectTypes.STORY_FEED,
        NomObjectTypes.SOCIAL_FEED,
        NomObjectTypes.CLIPPING_FEED,
        NomObjectTypes.BROADCAST_FEED,
        NomObjectTypes.COMBINED_FEED,
      ],
      objectMap,
      (objectIds) => ({
        datasource_object_id: objectIds,
        datasource_object_class_id: Array(objectIds?.length).fill(
          NomObjectClass.FEED,
        ),
      }),
      PopupType.SELECT_DATASOURCES,
    );
  }

  isChartOrMap(): boolean {
    return (
      this.widgetTypeId === WidgetType.WIDGET_TYPE_CHART ||
      this.widgetTypeId === WidgetType.WIDGET_TYPE_GEO_MAP
    );
  }

  chartCallback: Highcharts.ChartCallbackFunction = (chart) => {
    this.chartRef = chart;
    this.scheduleChartResize();
  };

  private scheduleChartResize(): void {
    requestAnimationFrame(() => {
      const rect = this.widgetContent?.nativeElement?.getBoundingClientRect();
      if (rect) {
        this.updateChartDimensions(rect.width, rect.height);
      }
    });
  }

  private resetWidgetState(): void {
    this.chartOptions = [];
    this.currentChartIndex = 0;
    this.chartConstructor = 'chart';
    this.chartRef = null;
    this.tableData = null;
    this.posts = null;
    this.removeIframe();
  }

  private handleDataChange(data: any): void {
    // Handle the change in data here
    const widgetContent = atob(data);
    const widgetContentJson = JSON.parse(widgetContent);
    this.hasResults = widgetContentJson.has_results ?? false;

    this.resetWidgetState();
    this.widgetItems = widgetContentJson.widget_data;

    switch (this.widgetTypeId) {
      case WidgetType.WIDGET_TYPE_CHART:
        this.processChartWidget(this.widgetItems);
        break;

      case WidgetType.WIDGET_TYPE_TABLE:
        //print the table in the console
        //console.log('table', this.widgetItems);
        this.tableData = this.widgetItems;
        break;

      case WidgetType.WIDGET_TYPE_GEO_MAP:
        this.initializeMap(this.widgetItems[0].data);
        break;
      case WidgetType.WIDGET_TYPE_POST_LIST:
        this.posts = this.widgetItems[0].posts;
        break;
      case WidgetType.WIDGET_TYPE_SUMMARY:
      case WidgetType.WIDGET_TYPE_HTML:
        if (this.hasResults) {
          this.createIframe();
        } else {
          this.removeIframe();
        }
        break;
    }
  }

  private removeIframe(): void {
    // Find an existing iframe if it exists

    const existingIframe = this.widgetContent?.nativeElement.querySelector(
      'iframe.html-widget-iframe',
    ) as HTMLIFrameElement | null;
    if (existingIframe?.parentNode) {
      this.renderer.removeClass(
        existingIframe.parentNode,
        'widget-content--html',
      );
      existingIframe.parentNode.removeChild(existingIframe);
    }
  }

  private createIframe(): void {
    // Find an existing iframe if it exists

    if (!this.widgetContent) {
      // No widget content element found and hence no iframe can be created
      // * In Preview Mode, ViewChild might not be ready yet - defer iframe creation
      if (this.widgetViewIninitialized && !this.usesDataQuery) {
        // * ViewChild should be available by now, if isn't - clear data to retry.
        // * Only preview/presentation cards own their widget copy, so mutating it
        // * is safe here; query-backed data is cache-owned and never mutated.
        this.widget.data = '';
      }
      // For preview mode, don't clear data - let ngAfterViewInit handle it
      return;
    }
    const existingIframe = this.widgetContent.nativeElement.querySelector(
      'iframe.html-widget-iframe',
    );

    let failOverHTML = '';
    if (!this.widgetItems?.[0]?.html) {
      const failOverMessage = this.translateService.instant(
        'widget.message.no-content-available',
      );
      failOverHTML =
        '<span class="widget-no-content"><p>' + failOverMessage + '</p></span>';
    }

    if (existingIframe) {
      // Update the srcdoc of the existing iframe
      existingIframe.srcdoc = this.withHtmlWidgetStyle(
        this.widgetItems?.[0]?.html ?? failOverHTML,
      );
    } else {
      // Create a new iframe element if it doesn't exist
      const iframe = this.renderer.createElement('iframe');
      iframe.className = 'html-widget-iframe';

      // Set sandbox attributes for security
      iframe.setAttribute(
        'sandbox',
        'allow-scripts allow-same-origin allow-popups allow-forms',
      );

      // Write the widgetHtml into the iframe
      iframe.srcdoc = this.withHtmlWidgetStyle(
        this.widgetItems?.[0]?.html ?? failOverHTML,
      );

      // Inject the iframe into the container
      const container = this.widgetContent.nativeElement;
      // Bottom padding lives on the host (see .widget-content--html): the
      // iframe body's own padding scrolls away with the content.
      this.renderer.addClass(container, 'widget-content--html');
      container.appendChild(iframe);
    }
  }
  /**
   * The server-rendered HTML widgets (Top Influencers, Summary, Country flags)
   * live in their own iframe document, so neither the app's design tokens nor
   * the `shared.sensika-scrollbar` mixin reach them, and the influencers
   * stylesheet even pins `html { overflow: hidden }`. This block, appended to
   * the document head, gives every HTML widget the same behaviour as the
   * Angular-rendered widgets: a scrolling document, the thin 5px scrollbar
   * (values mirror the `scrollbar()` mixin, thumb read from the live token at
   * render time so it follows the theme), and the widget body padding
   * (`--space-12`) in place of each template's own ad-hoc spacing. The bottom
   * padding is on the iframe host instead (`.widget-content--html`), because
   * padding inside the document scrolls away with the content.
   */
  private htmlWidgetStyle(): string {
    const track = 'rgba(180, 196, 209, 0.4)';
    const thumb = cssToken('--color-surface-deep');
    return (
      '<style>' +
      'html { overflow-y: auto !important; scrollbar-width: thin; }' +
      'html::-webkit-scrollbar { width: 5px; }' +
      `html::-webkit-scrollbar-track { background: ${track}; }` +
      'html::-webkit-scrollbar-thumb { ' +
      `background-color: ${thumb}; border-radius: 2.5px; border: 3px solid ${track}; }` +
      'body { height: auto !important; min-height: 100%; box-sizing: border-box; ' +
      'margin: 0 !important; padding: 12px 12px 0 !important; }' +
      '.table-responsive { overflow-y: visible !important; padding: 0 !important; }' +
      '.summary-box-container { padding: 0 !important; }' +
      '</style>'
    );
  }

  private withHtmlWidgetStyle(html: string): string {
    const style = this.htmlWidgetStyle();
    const headEnd = html.search(/<\/head>/i);
    // The no-content fallback has no <head>; the templates all have one.
    return headEnd === -1
      ? style + html
      : html.slice(0, headEnd) + style + html.slice(headEnd);
  }

  stringifyWithFunctions(obj) {
    return JSON.stringify(obj, (key, value) => {
      // If the value is a function, convert it to its string representation
      if (typeof value === 'function') {
        return value.toString();
      }
      return value;
    });
  }

  private processChartWidget(widgetItems: any[]): void {
    if (!this.utilsService.isIterable(widgetItems)) {
      return;
    }
    this.chartOptions = [];

    if (this.widgetPreview) {
      // destroy previous chart instance to prevent memory leaks and stale charts in preview mode
      this.updateChart();
    }

    for (const item of widgetItems) {
      if (typeof item.chart === 'undefined') {
        continue;
      }

      const chartOptions = atob(item.chart);
      // * a side map rather than the query cache: the only reader is the
      // * widget-settings "debug chart JSON" tab
      this.widgetViewState.setChartOptions(this.widget.id, chartOptions);

      let chartDataParsed = {};
      try {
        eval('chartDataParsed = ' + chartOptions + ';');
      } catch (error) {
        this.logger.error('Error parsing chart options:', error, chartOptions);
      }

      // Merge base responsive options
      const responsiveOptions = {
        chart: {
          backgroundColor: 'transparent',
          reflow: true,
          animation: false,
          spacing: [20, 20, 20, 20],
        },
      };

      chartDataParsed = {
        ...chartDataParsed,
        chart: {
          ...chartDataParsed['chart'],
          ...responsiveOptions.chart,
        },
      };

      this.chartOptions.push(chartDataParsed);
    }

    // Apply series click and xAxis zoom handlers to all chartOptions
    this.chartOptions.forEach((chart) => {
      if (chart?.series && !this.widgetPreview) {
        chart.series.forEach((series) => {
          series.point = {
            events: {
              click: this.onPointClick.bind(this),
            },
          };
        });
      }

      if (chart?.xAxis) {
        const xAxes = Array.isArray(chart.xAxis) ? chart.xAxis : [chart.xAxis];

        xAxes.forEach((axis: any) => {
          axis.events = {
            afterSetExtremes: (
              event: Highcharts.AxisSetExtremesEventObject,
            ) => {
              if (event.trigger === 'zoom') {
                const isResetZoom =
                  typeof event.userMin === 'undefined' &&
                  typeof event.userMax === 'undefined';

                const startTimestamp = Math.floor(
                  (isResetZoom ? event.dataMin : event.userMin) / 1000,
                );
                const endTimestamp = Math.floor(
                  (isResetZoom ? event.dataMax : event.userMax) / 1000,
                );

                const chart = (event.target as any).chart;
                const seriesPoint = chart.series?.[0]
                  ?.data?.[0] as Highcharts.Point;

                const options = JSON.parse(
                  JSON.stringify(seriesPoint?.options ?? {}),
                );

                this.store.dispatch(
                  widgetActions.widgetPointClicked({
                    widgetId: this.widget.id,
                    options: {
                      ...options,
                      zoom_start: startTimestamp,
                      zoom_end: endTimestamp,
                      is_reset_zoom: isResetZoom,
                    },
                  }),
                );
              }
            },
          };
        });
      }
    });
  }

  onPointClick(event: Highcharts.PointClickEventObject): void {
    this.handlePointClick(event);
  }

  private handlePointClick(event: Highcharts.PointClickEventObject): void {
    if (!event?.point?.options) return;

    // Deep clone to break the reference to Highcharts' internal object
    const options = JSON.parse(JSON.stringify(event.point.options));

    this.store.dispatch(
      widgetActions.widgetPointClicked({
        widgetId: this.widget.id,
        options,
      }),
    );
  }

  toggleWidgetSelect() {
    this.objectActionService.toggleObjectSelection(this.widget);
  }

  updateWidgetHeaderColor(color: string | null) {
    const widgetCopy = {
      ...this.widget,
      settings: {
        ...this.widget.settings,
        widget_header_color: color,
      },
    };

    this.widgetPersistence.updateWidget(this.widget.id, widgetCopy);
  }

  getSavedZoom(): { zoom: number; center: [number, number] } | null {
    const zoom = this.widget.settings?.['map_zoom'];
    const center = this.widget.settings?.['map_center'];

    if (
      typeof zoom === 'number' &&
      Array.isArray(center) &&
      center.length === 2 &&
      typeof center[0] === 'number' &&
      typeof center[1] === 'number'
    ) {
      return {
        zoom,
        center: center as [number, number],
      };
    }

    return null;
  }

  initializeMap(data: any[]) {
    // Prepare data for Highcharts
    const highchartsData = data.map((item) => {
      return {
        'hc-key': item.code.toLowerCase(), // Highcharts uses lowercase country codes
        value: item.y,
        raw: item.raw,
      };
    });

    this.chartConstructor = 'mapChart'; // ! Set the chart type to 'Map'

    const baseOptions = {
      chart: {
        map: WorldMapData,
        backgroundColor: 'transparent',
        reflow: true,
        spacing: [20, 20, 20, 20],
        events: {
          load: () => {
            const savedZoom = this.getSavedZoom();
            const mapChart = this.chartRef as Highcharts.MapChart;

            if (savedZoom && mapChart.mapView) {
              this.logger.debug('Restoring map zoom:', savedZoom);

              try {
                // ! clone "center" to prevent Highcharts from mutating a Frozen array
                const [x, y] = savedZoom.center;
                const safeCenter: [number, number] = [x, y];

                mapChart.mapView.setView(safeCenter, savedZoom.zoom);
              } catch (err) {
                this.logger.error('Failed to restore map zoom:', err);
              }
            }

            // * trigger initial size calculation after map is loaded
            setTimeout(() => {
              if (this.widgetContent?.nativeElement) {
                const rect =
                  this.widgetContent.nativeElement.getBoundingClientRect();
                this.updateChartDimensions(rect.width, rect.height);
              }
            }, 0);
          },
          redraw: () => {
            const mapChart = this.chartRef as Highcharts.MapChart;
            const mapView = mapChart.mapView;

            if (mapView) {
              const zoom = mapView.zoom;
              const center = mapView.center as [number, number];

              this.zoomChanged.emit({ zoom, center });
            } else {
              this.logger.warn('MapView not available during redraw');
            }
          },
        },
      },
      credits: {
        enabled: false,
      },
      title: {
        text: '',
      },
      animation: false,
      exporting: {
        enabled: false,
      },
      navigation: {
        buttonOptions: {
          enabled: true,
        },
      },
      mapNavigation: {
        enabled: true,
        enableDoubleClickZoom: true,
        enableMouseWheelZoom: true,
        enableTouchZoom: true,
        buttonOptions: {
          align: 'right',
        },
      },
      series: [
        {
          data: highchartsData,
          animation: false,
          mapData: WorldMapData,
          joinBy: 'hc-key',
          nullColor: '#D7D7D7', // Color for countries with no data
          name: '',
          states: {
            hover: {
              color:
                getComputedStyle(document.body)
                  .getPropertyValue('--color-action-primary-bg')
                  .trim() || '#0494eb',
            },
          },
          dataLabels: {
            enabled: true,
            formatter: function () {
              if (
                typeof this.point.value != 'undefined' &&
                this.point.value != null
              ) {
                return (
                  this.point['hc-key'] + ': ' + number_format(this.point.value)
                );
              }

              return this.point.value;
            },
          },
          legend: {
            layout: 'vertical',
            align: 'left',
            verticalAlign: 'top',
            borderRadius: 5,
          },
          point: {
            events: {
              click: (event: any) => {
                // * if already selected -> do nothing
                if (this.selectedCountryRawValue !== null) {
                  return;
                }

                const propertyUri = 'post_section_country_id';
                const name = (event.point as any).name;
                const rawValue = (event.point.options as any).raw;

                this.selectedCountryRawValue = rawValue;

                const myFilterItem: FilterItem = {
                  title: name,
                  kind: 'value',
                  raw_value: rawValue,
                  count_posts: 0,
                  icon: 'filter_icon',
                  checked: true,
                  active: false,
                  property_uri: propertyUri,
                  type: 'include',
                };

                this.store.dispatch(
                  selectFilterItem({
                    propertyUri: propertyUri,
                    filterItem: myFilterItem,
                    mode: 'include',
                  }),
                );

                this.store.dispatch(activateFilters());
              },
            },
          },
        },
      ],
    };

    this.chartOptions = [baseOptions];
  }

  deleteWidget() {
    const dialogRef = this.dialogService.openSimpleDialog(
      ConfirmDialogComponent,
      {
        data: {},
        closeOnClick: false,
      },
    );

    dialogRef.closed.subscribe(
      (event: DialogCloseOptions<{ reason: DialogCloseReason }>) => {
        if (event.reason === DialogCloseReason.Confirm) {
          this.widgetPersistence.deleteWidget(this.widget.id);
        } else if (event.reason === DialogCloseReason.Cancel) {
          console.log(`Action cancelled: ${event.reason}`);
        }
      },
    );
  }

  editWidget() {
    this.editService.editWidget(this.widget.id);
  }

  swapWidget() {
    const dialogRef = this.dialogService.openSimpleDialog(
      SwapWidgetDialogComponent,
      {
        data: {
          widget: this.widget,
          objectTypeId: this.currentObject.object_type_id,
          objectClassId: this.currentObject.object_class_id,
        },
        height: '70vh',
        width: '50vw',
        minWidth: 800,
        maxWidth: 1200,
        closeOnClick: false,
        closeOnEscape: true,
      },
    );

    dialogRef.closed.pipe(take(1)).subscribe((event: any) => {
      if (event.reason === DialogCloseReason.Confirm && event.data) {
        const result = event.data as SwapWidgetDialogOutput;

        this.widgetPersistence.swapWidgetType(
          this.widget.id,
          result.chartId,
          result.chartTypeId,
        );
      }
    });
  }

  onConfirm() {}

  onCancel() {}

  reloadWidget() {
    if (!this.widget.id || !this.usesDataQuery) {
      return;
    }

    // * a manual reload also counts as "attempted": the card may not have been
    // * marked visible yet, and refetch() is a no-op on a disabled query
    this.hasBeenVisible.set(true);
    this.dataQuery.refetch();
  }

  // * widget is scrolled into view

  handleVisibilityChange(isVisible: boolean): void {
    // * skip visibility tracking in Presentation Mode -> data is prefetched
    if (this.presentationModeActive) {
      return;
    }

    if (this.widget && this.widget.id) {
      this.widgetViewState.setVisible(this.widget.id, isVisible);
    }
    // * latch: enables the data query on first visibility
    if (isVisible) {
      this.hasBeenVisible.set(true);
    }
  }

  onItemClick(item: DropdownItem) {
    if (item.type === 'export-option') {
      this.exportChart(item.id);
    } else {
      item.action();
    }
  }

  private handleExport(exportFormat: WidgetExportFormat) {
    this.store
      .select(getContentFilters)
      .pipe(take(1))
      .subscribe((filters) => {
        this.widgetApi
          .exportWidget(
            this.widget.id,
            filters,
            NomLanguages.ENGLISH, // language can be dynamic based on user preference
            exportFormat,
          )
          .subscribe({
            next: (fileData: any) => {
              this.triggerDownload(
                fileData,
                `${this.widget.title}.${exportFormat}`,
              );
            },
            error: (err) => console.error('Export failed:', err),
          });
      });
  }

  private triggerDownload(data: any, fileName: string) {
    // 1. Extract extension (e.g., 'path/to/file.pdf' -> 'pdf')
    const extension = fileName.split('.').pop()?.toLowerCase() || '';

    // 2. Get the correct MIME type
    const mimeType = this.getMimeType(extension);

    // 3. Create the Blob with the dynamic type
    const blob = new Blob([data], { type: mimeType });

    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(url); // Important: Free up memory
  }

  private getMimeType(extension: string) {
    const types: Record<string, string> = {
      csv: 'text/csv;charset=utf-8;',
      pdf: 'application/pdf',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      png: 'image/png',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      svg: 'image/svg+xml',
    };
    // Fallback to octet-stream (generic binary) if extension is unknown
    return types[extension] || 'application/octet-stream';
  }

  exportChart(fileType: string) {
    switch (fileType) {
      case 'print':
        this.printWidget();
        break;
      case 'pdf':
      case 'png':
      case 'svg':
        this.chartRef?.exportChart(
          {
            type: this.getMimeType(fileType) as any,
            filename: this.widget.title,
          },
          {},
        );
        break;
      case 'jpeg':
        this.chartRef?.update({ chart: { backgroundColor: 'white' } });
        this.chartRef?.exportChart(
          {
            type: 'image/jpeg',
            filename: this.widget.title,
          },
          {},
        );
        break;
      case 'csv':
        this.handleExport('csv');
        break;
      case 'xlsx':
        this.handleExport('xlsx');
        break;
    }
  }

  shouldHideMessage(typeId: WidgetType): boolean {
    return (
      typeId === WidgetType.WIDGET_TYPE_CSV ||
      typeId === WidgetType.WIDGET_TYPE_EXPORT_VARIABLES_USER ||
      typeId === WidgetType.WIDGET_TYPE_EXPORT_VARIABLES_SYSTEM ||
      typeId === WidgetType.WIDGET_TYPE_SECTION_BREAK ||
      typeId === WidgetType.WIDGET_TYPE_PLACEHOLDER
    );
  }

  duplicateWidget() {
    this.store
      .pipe(
        select(selectCurrentObject),
        take(1),
        tap((currentObject) => {
          if (!currentObject) {
            return;
          }

          this.showWidgetDialog();
        }),
      )
      .subscribe();
  }

  // * Show the new dashboard widget dialog for single widget
  private showWidgetDialog(): void {
    this.store
      .pipe(
        select(selectCurrentObject),
        take(1),
        tap((currentObject) => {
          const dialogRef = this.dialogService.openSimpleDialog(
            MoveCopyWidgetDialogComponent,
            {
              data: {
                widgets: [this.widget],
                currentObject: currentObject,
              },
              closeOnClick: false,
            },
          );

          dialogRef.closed
            .pipe(take(1))
            .subscribe(
              (
                result: DialogCloseOptions<
                  { data: WidgetOperation[] } | undefined
                >,
              ) => {
                if (
                  result.reason === DialogCloseReason.Save &&
                  result.data?.data
                ) {
                  this.runWidgetMassOperation(result.data.data);
                }
              },
            );
        }),
      )
      .subscribe();
  }

  private runWidgetMassOperation(operations: WidgetOperation[]) {
    this.widgetPersistence.massOperation(operations);
  }

  changeIndex(index: number) {
    this.currentChartIndex = index;
  }

  selectWidgetPlaceholder() {
    let button: SidebarButtonConfig = {
      label: this.translateService.instant('feed-actions.label.widgets'),
      key: 'widgets',
      componentName: 'WidgetSidebarComponent',
      header: 'Widgets',
      icon: ['fas', 'chart-bar'],
    };
    this.sidebarService.selectComponent(button, true);

    this.store.dispatch(
      widgetActions.selectWidgetPlaceholderId({ id: this.widget.id }),
    );
  }

  removeFilter(dimension: Feed, $event: MouseEvent) {
    $event.stopPropagation();

    this.widgetPersistence.applySettings([this.widget.id], {
      dimension_id_filter: (
        this.widget.settings?.dimension_id_filter ?? []
      ).filter((id) => id !== dimension.id),
    });
  }

  removePivot(dimension: Feed, $event: MouseEvent) {
    $event.stopPropagation();

    this.widgetPersistence.applySettings([this.widget.id], {
      dimension_id_pivot: (
        this.widget.settings?.dimension_id_pivot ?? []
      ).filter((id) => id !== dimension.id),
    });
  }

  // In your component
  updateChart() {
    this.showChart = false; // Destroy the component
    this.cdr.detectChanges(); // Ensure the DOM updates

    this.showChart = true; // Re-create the component
  }
}
